import React, {useState, useEffect, useMemo} from 'react';
import ColorInput from './ColorInput';
import {auth} from '../firebaseConfig';
import { useGateLists } from '../hooks/useGateLists';
import DigitalAssetOfferControls from './DigitalAssetOfferControls';
import MintCollectionControls from './MintCollectionControls';
import MediaMintModule from './MediaMintModule';
import GateRevealControls from './GateRevealControls';
import { DONATION_TOKENS } from '../utils/donationTokens';

export default function ComponentControls({
                                              index,
                                              compId,
                                              comp,
                                              onMintCollectionChange,
                                              onMediaMintChange,
                                              mediaMintIsNew = false,
                                              mediaMintMediaCount,
                                              ownerPublicKey,
                                              mintCollectionIsNew = false,
                                              publishingComponent,
                                              publishingOwnerPublicKey,
                                              moveUp,
                                              moveDown,
                                              removeComponent,
                                              onEdit,

                                              gateType,
                                              selectedListId,
                                              extraGates = [],
                                              gateLogic = 'any',
                                              onExtraGatesChange,
                                              onGateLogicChange,
                                              onGateTypeChange,
                                              onGateListChange,
                                              donationCampaignOptions = [],
                                              donationGate = {},
                                              onDonationGateChange,
                                              directPaymentGate = {},
                                              onDirectPaymentGateChange,
                                              onDirectPaymentDraftChange,
                                              style = {},
                                              onStyleChange,
                                              coverUrl = '',
                                              onCoverChange,
                                              onPanelBackgroundImageChange,
                                              timestampDisplay = 'none',
                                              onTimestampDisplayChange,
                                              onShareToSocialFeed,

                                          }) {
    const lists = useGateLists();
    const [open, setOpen] = useState(false); // dropdown toggle
    const ownerUid = String(auth?.currentUser?.uid || "");
    const selectedDonationCampaign = donationCampaignOptions.find(
        (opt) => String(opt.id) === String(donationGate?.donationCompId || '')
    );
    const donationCurrency = selectedDonationCampaign?.currency || 'SOL';
    const gateTypeOptions = ['public', 'token', 'collection', 'compressedCollection', 'tokensHeld', 'donation', 'password', 'scheduledAccess', 'directPayment'];
    const listBackedGateTypes = ['token', 'collection', 'compressedCollection'];
    const gateTypeLabel = (t) => (
        t === 'compressedCollection'
            ? 'Compressed Collection'
            : t === 'tokensHeld'
                ? 'Tokens Held'
            : t === 'directPayment'
                ? 'Direct Payment'
                : t === 'scheduledAccess'
                    ? 'Scheduled Access'
                    : t.replace(/^\w/, (c) => c.toUpperCase())
    );
    const buildGateForType = (type, sourceGate = {}) => {
        const sameTypeListId = sourceGate?.type === type ? (sourceGate?.listId || '') : '';
        if (type === 'public') return {type: 'public'};
        if (type === 'token') return {type: 'token', listId: sameTypeListId, tokenIds: []};
        if (type === 'collection') return {type: 'collection', listId: sameTypeListId, collectionIds: []};
        if (type === 'compressedCollection') {
            return {
                type: 'compressedCollection',
                listId: sameTypeListId,
                compressedCollectionIds: []
            };
        }
        if (type === 'tokensHeld') {
            return {
                type: 'tokensHeld',
                listId: '',
                tokenThreshold: Number(sourceGate?.tokenThreshold || 0),
                currency: sourceGate?.currency || 'SOL',
                tokenMint: sourceGate?.tokenMint || '',
            };
        }
        if (type === 'donation') {
            return {
                type: 'donation',
                donationCompId: '',
                donationThreshold: 0,
                campaignName: '',
                currency: '',
                tokenMint: '',
            };
        }
        if (type === 'password') {
            return {
                type: 'password',
                password: String(sourceGate?.password || ''),
                passwordPrompt: String(sourceGate?.passwordPrompt || ''),
                targetId: String(sourceGate?.targetId || compId || index),
            };
        }
        if (type === 'scheduledAccess') {
            return {
                type: 'scheduledAccess',
                listId: '',
                scheduleName: String(sourceGate?.scheduleName || ''),
                startAt: String(sourceGate?.startAt || ''),
                endAt: String(sourceGate?.endAt || ''),
            };
        }
        if (type === 'directPayment') {
            return {
                type: 'directPayment',
                priceSol: Number(sourceGate?.priceSol || 0),
                payToUid: sourceGate?.payToUid || ownerUid,
                targetId: sourceGate?.targetId || String(compId ?? index),
                currency: sourceGate?.currency || 'SOL',
                tokenMint: sourceGate?.tokenMint || '',
            };
        }
        return {type: 'public'};
    };
    const handlePrimaryGateTypeSelect = (t) => {
        onGateTypeChange(index, t);
        if (!listBackedGateTypes.includes(t)) {
            onGateListChange(index, '');
        }
        if (t === 'tokensHeld') {
            const token = (DONATION_TOKENS || []).find(opt => opt.symbol === (donationGate?.currency || 'SOL'));
            onGateListChange(index, '', {
                tokenThreshold: Number(donationGate?.tokenThreshold || 0),
                currency: token?.symbol || 'SOL',
                tokenMint: token?.mint || '',
            });
        }
        if (t === 'password') {
            onGateListChange(index, '', {
                targetId: String(compId ?? index),
                passwordPrompt: String(donationGate?.passwordPrompt || ''),
            });
        }
        if (t === 'scheduledAccess') {
            onGateListChange(index, '', {
                scheduleName: String(donationGate?.scheduleName || ''),
                startAt: String(donationGate?.startAt || ''),
                endAt: String(donationGate?.endAt || ''),
            });
        }
    };

    const parseDecimal = (raw) => {
        const s = String(raw ?? "").trim().replace(",", ".");
        const n = parseFloat(s);
        return Number.isFinite(n) ? n : 0;
    };
    const [ccTheme, setCcTheme] = useState(() => {
        try {
            return document?.body?.classList?.contains('theme-dark') ? 'dark' : 'light';
        } catch {
            return 'light';
        }
    });
    const [directPaymentPriceInput, setDirectPaymentPriceInput] = useState(
        directPaymentGate?.priceSol != null ? String(directPaymentGate.priceSol) : ''
    );
    const [directPaymentCurrencyInput, setDirectPaymentCurrencyInput] = useState(
        directPaymentGate?.currency || 'SOL'
    );
    const [directPaymentFocused, setDirectPaymentFocused] = useState(false);
    const [donationThresholdInput, setDonationThresholdInput] = useState(
        donationGate?.donationThreshold != null ? String(donationGate.donationThreshold) : ''
    );
    const [donationThresholdFocused, setDonationThresholdFocused] = useState(false);
    const [tokensHeldThresholdInput, setTokensHeldThresholdInput] = useState(
        donationGate?.tokenThreshold != null ? String(donationGate.tokenThreshold) : ''
    );
    const [tokensHeldThresholdFocused, setTokensHeldThresholdFocused] = useState(false);
    const [extraPriceInputs, setExtraPriceInputs] = useState({});
    const [extraCurrencyInputs, setExtraCurrencyInputs] = useState({});
    const [extraPriceFocused, setExtraPriceFocused] = useState({});

    useEffect(() => {
        if (directPaymentFocused) return;
        const nextVal = directPaymentGate?.priceSol != null ? String(directPaymentGate.priceSol) : '';
        setDirectPaymentPriceInput(nextVal);
    }, [directPaymentGate?.priceSol, directPaymentFocused]);
    useEffect(() => {
        if (directPaymentFocused) return;
        const nextCurrency = directPaymentGate?.currency || 'SOL';
        setDirectPaymentCurrencyInput(nextCurrency);
    }, [directPaymentGate?.currency, directPaymentFocused]);
    useEffect(() => {
        if (donationThresholdFocused) return;
        const nextVal = donationGate?.donationThreshold != null
            ? String(donationGate.donationThreshold)
            : '';
        setDonationThresholdInput(nextVal);
    }, [donationGate?.donationThreshold, donationThresholdFocused]);
    useEffect(() => {
        if (tokensHeldThresholdFocused) return;
        const nextVal = donationGate?.tokenThreshold != null
            ? String(donationGate.tokenThreshold)
            : '';
        setTokensHeldThresholdInput(nextVal);
    }, [donationGate?.tokenThreshold, tokensHeldThresholdFocused]);

    useEffect(() => {
        if (!Array.isArray(extraGates) || extraGates.length === 0) return;
        setExtraPriceInputs((prev) => {
            let changed = false;
            const next = { ...prev };
            extraGates.forEach((g, idx) => {
                const key = String(idx);
                if (extraPriceFocused[key]) return;
                if (g?.type !== 'directPayment') return;
                const nextVal = g?.priceSol != null ? String(g.priceSol) : '';
                if (next[key] !== nextVal) {
                    next[key] = nextVal;
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
        setExtraCurrencyInputs((prev) => {
            let changed = false;
            const next = { ...prev };
            extraGates.forEach((g, idx) => {
                const key = String(idx);
                if (g?.type !== 'directPayment') return;
                const nextCurrency = g?.currency || 'SOL';
                if (next[key] !== nextCurrency) {
                    next[key] = nextCurrency;
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    }, [extraGates, extraPriceFocused]);

    useEffect(() => {
        if (typeof document === 'undefined') return;
        const syncThemeFromBody = () => {
            const nextTheme = document?.body?.classList?.contains('theme-dark') ? 'dark' : 'light';
            setCcTheme((prev) => (prev === nextTheme ? prev : nextTheme));
        };

        syncThemeFromBody();
        if (typeof MutationObserver === 'undefined') return;
        const observer = new MutationObserver(() => syncThemeFromBody());
        observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
        return () => observer.disconnect();
    }, []);

    // ADD: collapsible sections state
    const [ccOpen, setCcOpen] = useState({
        background: false,
        font: false,
        borders: false,
        gates: false,
    });

// ADD: helper to toggle a section
    const toggleCcSection = (key) => setCcOpen(s => ({...s, [key]: !s[key]}));




    const updateStyle = changes => onStyleChange(index, {...style, ...changes});

    // Background handlers
    const handleBgTypeChange = e =>
        updateStyle({backgroundType: e.target.value, backgroundImage: '', backgroundColor: ''});
    const handleColorChange = e =>
        updateStyle({backgroundColor: e.target.value, backgroundImage: ''});

    const handleImageChange = e => {
        const file = e.target.files?.[0];
        if (!file) return;
        onPanelBackgroundImageChange(index, file);
    };
    const handleCoverSelect = e => {
        const file = e.target.files?.[0];
        if (file) onCoverChange(index, file);
    };

    // Gradient helpers
    const initGrad = style.gradient || {colors: ['#ffffff', '#000000'], direction: 'to right'};
    const handleGradientColor = (pos, value) => {
        const grad = {...initGrad, colors: [...initGrad.colors]};
        grad.colors[pos] = value;
        updateStyle({
            backgroundImage: `linear-gradient(${grad.direction}, ${grad.colors[0]}, ${grad.colors[1]})`,
            gradient: grad,
            backgroundColor: ''
        });
    };
    const handleGradientDirection = e => {
        const grad = {...initGrad, direction: e.target.value};
        updateStyle({
            backgroundImage: `linear-gradient(${e.target.value}, ${grad.colors[0]}, ${grad.colors[1]})`,
            gradient: grad,
            backgroundColor: ''
        });
    };

    // Border handlers
    const handleBorderColorChange = e => updateStyle({borderColor: e.target.value});
    const handleBorderStyleChange = e => updateStyle({borderStyle: e.target.value});
    const handleBorderWidthChange = e => updateStyle({borderWidth: `${e.target.value}px`});

    // ADD: local theme just for this component (light | dark)


// ADD: colors used ONLY inside ComponentControls
    const CC_PALETTES = {
        light: {
            surface: '#ffffff',
            surfaceSoft: '#f7f7fb',
            text: '#0f172a',
            textMuted: '#475569',
            border: '#CBD5E1',
            controlBg: '#ffffff',
            controlText: '#0f172a',
            controlBorder: '#94A3B8',
            buttonBg: '#ffffff',
            buttonBgHover: '#E2E8F0',
            focus: '#60A5FA'
        },
        dark: {
            surface: '#0b1220',
            surfaceSoft: '#0f172a',
            text: '#E2E8F0',
            textMuted: '#94A3B8',
            border: '#273244',
            controlBg: '#111827',
            controlText: '#E5E7EB',
            controlBorder: '#334155',
            buttonBg: '#111827',
            buttonBgHover: '#1f2937',
            focus: '#38BDF8'
        }
    };
    const CC = CC_PALETTES[ccTheme];

// ADD: tiny style helpers you can reuse inline
    const ccStyles = {
        container: {
            background: CC.surface,
            color: CC.text,
            border: `1px solid ${CC.border}`,
            borderRadius: 12,
            padding: 12
        },
        label: {
            display: 'grid',
            gap: 6,
            background: CC.surfaceSoft,
            color: CC.text,             // label text color
            border: `1px solid ${CC.border}`,
            borderRadius: 10,
            padding: 8,
            margin: 3,
            // width: '100%'
        },
        heading: {
            background: 'transparent',
            border: 'none',
            color: CC.text,
            fontSize: 14,
            fontWeight: 600,
            margin: '8px 0 4px 0',
            padding: 0
        },
        labelHint: {fontSize: 12, color: CC.textMuted},
        control: {
            width: '100%',
            background: CC.controlBg,
            color: CC.controlText,      // input text color
            border: `1px solid ${CC.controlBorder}`,
            borderRadius: 10,
            padding: '6px 10px',
            outline: 'none'
        },
        button: {
            background: CC.buttonBg,
            color: CC.controlText,
            border: `1px solid ${CC.controlBorder}`,
            borderRadius: 10,
            padding: '6px 10px',
            cursor: 'pointer'
        },
        title: {
            background: 'transparent',
            border: 'none',
            color: CC.text,
            fontSize: 18,
            fontWeight: 700,
            margin: '4px 0 10px 0',
            padding: 0,
            textAlign: 'left',
            letterSpacing: '0.2px',
            textDecoration: 'underline'
        }
    };



    const bgType = style.backgroundType || 'color';


    const ComponentControlSection = useMemo(() => ({title, isOpen, onToggle, children}) => (
        <div style={{margin: '12px 0', borderTop: `1px solid ${CC.border}`, paddingTop: 8}}>
            {/* Clickable header row */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    padding: '4px 0',
                    userSelect: 'none',
                    color: CC.text,
                    fontWeight: 600,
                    fontSize: 14,
                }}
                onClick={onToggle}
                aria-expanded={isOpen}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onToggle()}
            >
                <span>{title}</span>
                <span
                    style={{
                        display: 'inline-block',
                        transition: 'transform 0.25s ease',
                        transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                        fontSize: 14,
                        color: CC.textMuted,
                    }}
                >
        ▸
      </span>
            </div>

            {/* Collapsible body */}
            {isOpen && <div style={{marginTop: 6}}>{children}</div>}
        </div>
    ), [CC.border, CC.text, CC.textMuted]);


    // Only render in editor mode when all handlers are present
    if (
        typeof index !== 'number' ||
        typeof moveUp !== 'function' ||
        typeof moveDown !== 'function' ||
        typeof removeComponent !== 'function' ||
        typeof onEdit !== 'function' ||
        typeof onStyleChange !== 'function' ||
        typeof onCoverChange !== 'function' ||
        typeof onPanelBackgroundImageChange !== 'function' ||
        typeof onGateTypeChange !== 'function' ||
        typeof onGateListChange !== 'function'

    )
        return null;

    return (
        <div className="component-controls" style={ccStyles.container}>
            {/* Header / toolbar */}
            <div className="cc-header">
                <div className="cc-title">
                    <span className="cc-title-text" style={ccStyles.labelHint}>Edit Component</span>
                </div>

                <div className="cc-toolbar">
                    <div className="cc-group">
                        <button onClick={() => moveUp(index)} style={ccStyles.button} title="Move up">↑</button>
                        <button onClick={() => moveDown(index)} style={ccStyles.button} title="Move down">↓</button>
                        <button onClick={() => onEdit(index)} style={ccStyles.button} title="Edit content">Edit
                            Content
                        </button>
                        {typeof onShareToSocialFeed === 'function' && (
                            <button
                                type="button"
                                onClick={onShareToSocialFeed}
                                style={ccStyles.button}
                                title="Share this component to the social feed"
                            >
                                Share to Feed
                            </button>
                        )}
                        <button onClick={() => removeComponent(index)} style={ccStyles.button}
                                title="Remove component">Remove Component
                        </button>
                    </div>
                    <div className="cc-group">
                        <button
                            type="button"
                            className="cc-toggle"
                            aria-expanded={open}
                            aria-controls={`cc-body-${index}`}
                            onClick={() => setOpen(v => !v)}
                            title={open ? 'Collapse controls' : 'Expand controls'}
                            style={ccStyles.button}
                        >
                            {open ? '▾ Hide Style and Gates' : '▸ Edit Style and Gates'}
                        </button>
                    </div>
                </div>
            </div>

            {(publishingComponent || comp)?.type === 'mint' && typeof onMintCollectionChange === 'function' && (
                <MintCollectionControls comp={publishingComponent || comp} ownerPublicKey={publishingOwnerPublicKey || ownerUid}
                    isNew={mintCollectionIsNew} onChange={collectionId => onMintCollectionChange(index, collectionId)} />
            )}
            <MediaMintModule comp={publishingComponent || comp}
                ownerPublicKey={publishingOwnerPublicKey || ownerPublicKey || ownerUid}
                isNew={mediaMintIsNew} mediaCount={mediaMintMediaCount}
                onChange={typeof onMediaMintChange === 'function' ? value => onMediaMintChange(index, value) : undefined} />
            {publishingComponent?.type === 'mint' && (
                <DigitalAssetOfferControls comp={publishingComponent} ownerPublicKey={publishingOwnerPublicKey || ownerUid} />
            )}
            {/* Body (collapsible) */}
            <div id={`cc-body-${index}`} className={`cc-body ${open ? 'open' : ''}`} hidden={!open}>

                <h1 style={ccStyles.title}>Customize Style and Gates</h1>


                <label style={ccStyles.label}>
                    Show timestamp:
                    <select
                        value={timestampDisplay}
                        onChange={e => onTimestampDisplayChange?.(e.target.value)}
                        style={ccStyles.control}
                    >
                        <option value="none">None</option>
                        <option value="created">Created</option>
                        <option value="updated">Updated</option>
                        <option value="both">Both</option>
                    </select>
                </label>

                {/* Row: Text & alignment */}


                <ComponentControlSection
                    title="Background Controls"
                    isOpen={ccOpen.background}
                    onToggle={() => toggleCcSection('background')}
                >
                    {/* Row: Background */}
                    <div className="cc-row">
                        <label className="cc-field" style={ccStyles.label}>
                            <span style={ccStyles.labelHint}>Background Type</span>
                            <select
                                value={bgType}
                                onChange={handleBgTypeChange}
                                style={ccStyles.control}
                            >
                                <option value="color">Color</option>
                                <option value="image">Image</option>
                                <option value="cover">Cover</option>
                                <option value="gradient">Gradient</option>
                            </select>
                        </label>

                        {bgType === 'color' && (
                            <label className="cc-field" style={ccStyles.label}>
                                <span style={ccStyles.labelHint}>Color</span>
                                <ColorInput
                                    value={style.backgroundColor || '#ffffff'}
                                    onChange={handleColorChange}
                                    style={ccStyles.control}
                                />
                            </label>
                        )}

                        {bgType === 'image' && (
                            <label className="cc-field" style={ccStyles.label}>
                                <span style={ccStyles.labelHint}>Upload Image</span>
                                <input type="file" accept="image/*"
                                       style={ccStyles.control}
                                       onChange={handleImageChange}/>
                            </label>
                        )}

                        {bgType === 'cover' && (
                            <label className="cc-field">
                                <span>Upload Cover</span>
                                <input type="file"
                                       accept="image/*"
                                       onChange={handleCoverSelect}
                                       style={ccStyles.control}
                                />
                            </label>
                        )}

                        {bgType === 'gradient' && (
                            <>
                                <label className="cc-field" style={ccStyles.label}>
                                    <span style={ccStyles.labelHint}>From</span>
                                    <ColorInput
                                        value={style.gradient?.colors?.[0] || '#ffffff'}
                                        onChange={e => handleGradientColor(0, e.target.value)}
                                        style={ccStyles.control}
                                    />
                                </label>
                                <label className="cc-field" style={ccStyles.label}>
                                    <span style={ccStyles.labelHint}>To</span>
                                    <ColorInput
                                        value={style.gradient?.colors?.[1] || '#000000'}
                                        onChange={e => handleGradientColor(1, e.target.value)}
                                        style={ccStyles.control}
                                    />
                                </label>
                                <label className="cc-field" style={ccStyles.label}>
                                    <span style={ccStyles.labelHint}>Dir</span>
                                    <select
                                        value={style.gradient?.direction || 'to right'}
                                        onChange={handleGradientDirection}
                                        style={ccStyles.control}
                                    >
                                        <option value="to right">→</option>
                                        <option value="to bottom">↓</option>
                                        <option value="45deg">↘</option>
                                    </select>
                                </label>
                            </>
                        )}
                    </div>
                </ComponentControlSection>


                {/* Row: Typography overrides */}
                <div className="cc-row cc-row--stack">


                    <ComponentControlSection
                        title="Font Controls"
                        isOpen={ccOpen.font}
                        onToggle={() => toggleCcSection('font')}
                    >
                        <div className="cc-row">


                            <label className="cc-field" style={ccStyles.label}>
                                <span style={ccStyles.labelHint}>Text Align</span>
                                <select
                                    value={style.textAlign || 'left'}
                                    onChange={e => updateStyle({textAlign: e.target.value})}
                                    style={ccStyles.control}
                                >
                                    <option value="left">Left</option>
                                    <option value="center">Center</option>
                                    <option value="right">Right</option>
                                    <option value="justify">Justify</option>
                                </select>
                            </label>
                        </div>


                        <label className="cc-field" style={ccStyles.label}>
                            <span style={ccStyles.labelHint}>General Font Color</span>
                            <ColorInput
                                value={style.color || '#000000'}
                                onChange={e => updateStyle({color: e.target.value})}
                                style={ccStyles.control}
                            />
                        </label>

                        <div className="cc-inline" style={ccStyles.label}>
                            <button
                                type="button"
                                onClick={() => updateStyle({color: '#000000'})}
                                style={ccStyles.control}
                            >
                                Black
                            </button>
                            <button
                                type="button"
                                onClick={() => updateStyle({color: '#FFFFFF'})}
                                style={ccStyles.control}
                            >White
                            </button>
                        </div>

                        {/* HEADER COLOR (panel override) */}
                        <label className="cc-field" style={ccStyles.label}>
                            <span style={ccStyles.labelHint}>Title Color</span>
                            <ColorInput
                                value={style.headers?.color || ''}
                                onChange={e =>
                                    updateStyle({
                                        headers: {...(style.headers || {}), color: e.target.value || undefined}
                                    })
                                }
                                style={ccStyles.control}
                            />
                        </label>

                        {/* PARAGRAPH/CAPTION COLOR (panel override) */}
                        <label className="cc-field" style={ccStyles.label}>
                            <span style={ccStyles.labelHint}>Paragraph Color</span>
                            <ColorInput
                                value={style.paragraph?.color || ''}
                                onChange={e =>
                                    updateStyle({
                                        paragraph: {...(style.paragraph || {}), color: e.target.value || undefined}
                                    })
                                }
                                style={ccStyles.control}
                            />
                        </label>


                        <div className="cc-field">
                            <span style={ccStyles.labelHint}>Header Font Family</span>
                            <select
                                value={style.headers?.fontFamily || ''}
                                onChange={e => updateStyle({
                                    headers: {
                                        ...(style.headers || {}),
                                        fontFamily: e.target.value
                                    }
                                })}
                                style={ccStyles.control}
                            >
                                <option value="">— use global —</option>
                                <option value="system-ui">system-ui</option>
                                <option value="Inter, sans-serif">Inter, sans-serif</option>
                                <option value="Roboto, sans-serif">Roboto, sans-serif</option>
                                <option value="Montserrat, sans-serif">Montserrat, sans-serif</option>
                                <option value="Georgia, serif">Georgia, serif</option>
                                <option value="'Times New Roman', serif">Times New Roman, serif</option>
                                <option value="'Courier New', monospace">Courier New, monospace</option>
                            </select>
                        </div>
                        <label className="cc-field cc-field--small" style={ccStyles.label}>
                            <span style={ccStyles.labelHint}>Header Size</span><span className="cc-unit">px</span>
                            <input type="number" min="10" max="96"
                                   value={style.headers?.fontSize ?? ''}
                                   onChange={e => updateStyle({
                                       headers: {
                                           ...(style.headers || {}),
                                           fontSize: Number(e.target.value) || undefined
                                       }
                                   })}
                                   style={ccStyles.control}
                            />
                        </label>
                        <label className="cc-field cc-field--small" style={ccStyles.label}>
                            <span style={ccStyles.labelHint}>Header Weight</span>
                            <select
                                value={style.headers?.fontWeight ?? ''}
                                onChange={e => updateStyle({
                                    headers: {
                                        ...(style.headers || {}),
                                        fontWeight: e.target.value ? Number(e.target.value) : undefined
                                    }
                                })}
                                style={ccStyles.control}

                            >
                                <option value="">— global —</option>
                                {[400, 500, 600, 700, 800].map(w => <option key={w} value={w}>{w}</option>)}
                            </select>
                        </label>

                        <div className="cc-field">
                            <span style={ccStyles.labelHint}>Paragraph Family</span>
                            <select
                                value={style.paragraph?.fontFamily || ''}
                                onChange={e => updateStyle({
                                    paragraph: {
                                        ...(style.paragraph || {}),
                                        fontFamily: e.target.value
                                    }
                                })}
                                style={ccStyles.control}
                            >
                                <option value="">— use global —</option>
                                <option value="system-ui">system-ui</option>
                                <option value="Inter, sans-serif">Inter, sans-serif</option>
                                <option value="Roboto, sans-serif">Roboto, sans-serif</option>
                                <option value="Montserrat, sans-serif">Montserrat, sans-serif</option>
                                <option value="Georgia, serif">Georgia, serif</option>
                                <option value="'Times New Roman', serif">Times New Roman, serif</option>
                                <option value="'Courier New', monospace">Courier New, monospace</option>
                            </select>
                        </div>
                        <label className="cc-field cc-field--small" style={ccStyles.label}>
                            <span style={ccStyles.labelHint}>Paragraph Size</span><span className="cc-unit">px</span>
                            <input type="number" min="10" max="48"
                                   value={style.paragraph?.fontSize ?? ''}
                                   onChange={e => updateStyle({
                                       paragraph: {
                                           ...(style.paragraph || {}),
                                           fontSize: Number(e.target.value) || undefined
                                       }
                                   })}
                                   style={ccStyles.control}
                            />
                        </label>

                        <div className="cc-field">
                            <span style={ccStyles.labelHint}>Link Color</span>
                            <ColorInput
                                value={style.links?.color || ''}
                                onChange={e => updateStyle({links: {...(style.links || {}), color: e.target.value}})}
                                style={ccStyles.control}
                            />
                        </div>
                        <div className="cc-field">
                            <span style={ccStyles.labelHint}>Link Decoration</span>
                            <select
                                value={style.links?.textDecoration || ''}
                                onChange={e => updateStyle({
                                    links: {
                                        ...(style.links || {}),
                                        textDecoration: e.target.value
                                    }
                                })}
                                style={ccStyles.control}
                            >
                                <option value="">— global —</option>
                                {['none', 'underline', 'overline', 'line-through'].map(d => <option key={d}
                                                                                                    value={d}>{d}</option>)}
                            </select>
                        </div>

                        <div>
                            <button
                                type="button"
                                className="muulti-button"
                                onClick={() => onStyleChange(index, {
                                    ...style,
                                    headers: undefined,
                                    paragraph: undefined,
                                    links: undefined
                                })}
                                style={ccStyles.button}
                            >
                                Reset to Global Typography
                            </button>
                        </div>
                    </ComponentControlSection>
                </div>


                {/* Row: Border */}
                <ComponentControlSection
                    title="Border Controls"
                    isOpen={ccOpen.borders}
                    onToggle={() => toggleCcSection('borders')}
                >

                    <div className="cc-row">
                        <label className="cc-field" style={ccStyles.label}>
                            <span style={ccStyles.labelHint}>Border Style</span>
                            <select
                                value={style.borderStyle || 'solid'}
                                onChange={handleBorderStyleChange}
                                style={ccStyles.control}
                            >
                                <option value="solid">Solid</option>
                                <option value="dashed">Dashed</option>
                                <option value="dotted">Dotted</option>
                                <option value="double">Double</option>
                                <option value="none">None</option>
                            </select>
                        </label>


                        <label className="cc-field cc-field--small" style={ccStyles.label}>
                            <span style={ccStyles.labelHint}>Border Width</span><span className="cc-unit">px</span>
                            <div className="cc-number">
                                <input
                                    type="number"
                                    min="0"
                                    max="20"
                                    value={parseInt(style.borderWidth, 10) || 1}
                                    onChange={handleBorderWidthChange}
                                    style={ccStyles.control}
                                />

                            </div>
                        </label>

                        <label className="cc-field" style={ccStyles.label}>
                            <span>Border Color</span>
                            <ColorInput
                                value={style.borderColor || '#000000'}
                                onChange={handleBorderColorChange}
                                style={ccStyles.control}
                            />
                        </label>
                    </div>
                </ComponentControlSection>
                <ComponentControlSection
                    title="Access Controls"
                    isOpen={ccOpen.gates}
                    onToggle={() => toggleCcSection('gates')}
                >
                    <GateRevealControls style={style} onChange={updateStyle} ccStyles={ccStyles} />
                    {/* Row: Gate */}
                    <div className="cc-row cc-row--wrap">

                        <div className="cc-field">
                            <span style={ccStyles.labelHint}>Gate</span>
                            <div className="segmented" style={{display: 'flex', flexWrap: 'wrap', gap: 8}}>
                                {gateTypeOptions.map((t) => (
                                    <button
                                        key={t}
                                        type="button"
                                        className={`seg-btn ${gateType === t ? 'active' : ''}`}
                                        onClick={() => handlePrimaryGateTypeSelect(t)}
                                    >
                                        {gateTypeLabel(t)}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {gateType !== 'public' && gateType !== 'tokensHeld' && gateType !== 'donation' && gateType !== 'directPayment' && gateType !== 'password' && gateType !== 'scheduledAccess' && (
                            <label className="cc-field" style={ccStyles.label}>
                            <span style={ccStyles.labelHint}>
                              {gateType === 'token'
                                  ? 'Token List'
                                  : gateType === 'collection'
                                      ? 'Collection List'
                                      : 'Compressed Collection List'}
                            </span>
                                <select
                                    value={selectedListId || ''}
                                    onChange={(e) => onGateListChange(index, e.target.value)}
                                    style={ccStyles.control}
                                >
                                    <option value="">— none —</option>
                                    {lists
                                        .filter((l) => l.gateType === gateType)
                                        .map((l) => (
                                            <option key={l.id} value={l.id}>
                                                {l.name}
                                            </option>
                                        ))}
                                </select>
                            </label>
                        )}


                        {gateType === 'tokensHeld' && (() => {
                            const heldCurrency = donationGate?.currency || 'SOL';
                            const heldToken = (DONATION_TOKENS || []).find(t => t.symbol === heldCurrency);
                            return (
                                <div className="cc-field cc-field--small" style={ccStyles.label}>
                                    <span style={ccStyles.labelHint}>Token</span>
                                    <select
                                        value={heldCurrency}
                                        onChange={(e) => {
                                            const nextCurrency = e.target.value;
                                            const token = (DONATION_TOKENS || []).find(t => t.symbol === nextCurrency);
                                            const threshold = parseDecimal(tokensHeldThresholdInput);
                                            onGateListChange(index, '', {
                                                tokenThreshold: Number.isFinite(threshold) ? threshold : Number(donationGate?.tokenThreshold || 0),
                                                currency: nextCurrency,
                                                tokenMint: token?.mint || '',
                                            });
                                        }}
                                        style={ccStyles.control}
                                    >
                                        {(DONATION_TOKENS || []).map((t) => (
                                            <option key={t.symbol} value={t.symbol}>{t.symbol}</option>
                                        ))}
                                    </select>
                                    <span style={{ ...ccStyles.labelHint, marginTop: 8 }}>Minimum Held ({heldCurrency})</span>
                                    <input
                                        className="muulti-input"
                                        type="text"
                                        inputMode="decimal"
                                        value={tokensHeldThresholdInput}
                                        onChange={(e) => {
                                            const raw = e.target.value;
                                            setTokensHeldThresholdInput(raw);
                                            onGateListChange(index, '', {
                                                tokenThreshold: parseDecimal(raw),
                                                currency: heldCurrency,
                                                tokenMint: heldToken?.mint || '',
                                            });
                                        }}
                                        onFocus={() => setTokensHeldThresholdFocused(true)}
                                        onBlur={(e) => {
                                            setTokensHeldThresholdFocused(false);
                                            const n = parseDecimal(e.target.value);
                                            onGateListChange(index, '', {
                                                tokenThreshold: Number.isFinite(n) ? n : 0,
                                                currency: heldCurrency,
                                                tokenMint: heldToken?.mint || '',
                                            });
                                        }}
                                        style={ccStyles.control}
                                    />
                                    <div className="cc-hint" style={{marginTop: 6}}>
                                        Viewers must hold at least this amount in their connected wallet.
                                    </div>
                                </div>
                            );
                        })()}


                        {/* Donation-specific controls */}
                        {gateType === 'donation' && (
                            <>
                                <label className="cc-field" style={ccStyles.label}>
                                    <span style={ccStyles.labelHint}>Campaign</span>
                                    <select
                                        value={String(donationGate?.donationCompId || '')}
                                        onChange={(e) => {
                                            const nextId = e.target.value;
                                            const selected = donationCampaignOptions.find(
                                                (opt) => String(opt.id) === String(nextId)
                                            );
                                            const nextThreshold = parseDecimal(donationThresholdInput);
                                            onDonationGateChange?.({
                                                donationCompId: nextId,
                                                donationThreshold: Number.isFinite(nextThreshold) ? nextThreshold : (donationGate?.donationThreshold ?? 0),
                                                campaignName: selected?.campaignName || '',
                                                currency: selected?.currency || '',
                                                tokenMint: selected?.tokenMint || '',
                                            });
                                        }}
                                        style={ccStyles.control}
                                    >
                                        <option value="">— select a campaign —</option>
                                        {donationCampaignOptions.map(opt => (
                                            <option key={opt.id} value={opt.id}>{opt.label}</option>
                                        ))}
                                    </select>
                                </label>
                                <label className="cc-field cc-field--small" style={ccStyles.label}>
                                    <span style={ccStyles.labelHint}>Threshold ({donationCurrency})</span>
                                    <input
                                        type="number" min="0" step="0.000001"
                                        value={donationThresholdInput}
                                        onChange={(e) => {
                                            const raw = e.target.value;
                                            setDonationThresholdInput(raw);
                                            onDonationGateChange?.({
                                                donationCompId: donationGate?.donationCompId || '',
                                                donationThreshold: parseDecimal(raw),
                                                campaignName: donationGate?.campaignName || selectedDonationCampaign?.campaignName || '',
                                                currency: donationGate?.currency || selectedDonationCampaign?.currency || '',
                                                tokenMint: donationGate?.tokenMint || selectedDonationCampaign?.tokenMint || '',
                                            });
                                        }}
                                        onFocus={() => setDonationThresholdFocused(true)}
                                        onBlur={(e) => {
                                            setDonationThresholdFocused(false);
                                            const raw = e.target.value;
                                            const n = parseDecimal(raw);
                                            onDonationGateChange?.({
                                                donationCompId: donationGate?.donationCompId || '',
                                                donationThreshold: Number.isFinite(n) ? n : 0,
                                                campaignName: donationGate?.campaignName || selectedDonationCampaign?.campaignName || '',
                                                currency: donationGate?.currency || selectedDonationCampaign?.currency || '',
                                                tokenMint: donationGate?.tokenMint || selectedDonationCampaign?.tokenMint || '',
                                            });
                                        }}
                                        style={ccStyles.control}
                                    />
                                </label>
                            </>
                        )}

                        {gateType === 'password' && (
                            <div className="cc-field cc-field--small" style={ccStyles.label}>
                                <span style={ccStyles.labelHint}>Password</span>
                                <input
                                    className="muulti-input"
                                    type="text"
                                    autoComplete="off"
                                    value={String(donationGate?.password || '')}
                                    onChange={(e) => {
                                        onGateListChange(index, selectedListId || '', {
                                            password: e.target.value,
                                            targetId: String(compId ?? index),
                                        });
                                    }}
                                    style={ccStyles.control}
                                />
                                <span style={{ ...ccStyles.labelHint, marginTop: 8 }}>Prompt Text (optional)</span>
                                <input
                                    className="muulti-input"
                                    type="text"
                                    autoComplete="off"
                                    value={String(donationGate?.passwordPrompt || '')}
                                    onChange={(e) => {
                                        onGateListChange(index, selectedListId || '', {
                                            passwordPrompt: e.target.value,
                                            targetId: String(compId ?? index),
                                        });
                                    }}
                                    placeholder="Enter password to unlock this panel."
                                    style={ccStyles.control}
                                />
                                <div className="cc-hint" style={{marginTop: 6}}>
                                    Viewers must enter this password to unlock this panel.
                                </div>
                            </div>
                        )}

                        {gateType === 'scheduledAccess' && (
                            <div className="cc-field cc-field--small" style={ccStyles.label}>
                                <span style={ccStyles.labelHint}>Schedule Name</span>
                                <input
                                    className="muulti-input"
                                    type="text"
                                    autoComplete="off"
                                    value={String(donationGate?.scheduleName || '')}
                                    onChange={(e) => {
                                        onGateListChange(index, '', {
                                            scheduleName: e.target.value,
                                        });
                                    }}
                                    placeholder="Early Access Window"
                                    style={ccStyles.control}
                                />
                                <span style={{ ...ccStyles.labelHint, marginTop: 8 }}>Start Date & Time</span>
                                <input
                                    className="muulti-input"
                                    type="datetime-local"
                                    required
                                    value={String(donationGate?.startAt || '')}
                                    onChange={(e) => {
                                        onGateListChange(index, '', {
                                            startAt: e.target.value,
                                        });
                                    }}
                                    style={ccStyles.control}
                                />
                                <span style={{ ...ccStyles.labelHint, marginTop: 8 }}>End Date & Time (optional)</span>
                                <input
                                    className="muulti-input"
                                    type="datetime-local"
                                    value={String(donationGate?.endAt || '')}
                                    onChange={(e) => {
                                        onGateListChange(index, '', {
                                            endAt: e.target.value,
                                        });
                                    }}
                                    style={ccStyles.control}
                                />
                            </div>
                        )}


                        {/* Direct Payment controls */}
                        {gateType === 'directPayment' && (() => {
                            return (
                                <div className="cc-field cc-field--small" style={ccStyles.label}>
                                    <span style={ccStyles.labelHint}>Price ({directPaymentCurrencyInput})</span>
                                    <input
                                        className="muulti-input"
                                        type="text"
                                        inputMode="decimal"
                                        value={directPaymentPriceInput}
                                        onChange={(e) => {
                                            const raw = e.target.value;
                                            onDirectPaymentDraftChange?.({
                                                compId,
                                                index,
                                                scope: "primary",
                                                priceSol: parseDecimal(raw),
                                            });
                                            setDirectPaymentPriceInput(raw);
                                        }}
                                        onFocus={() => setDirectPaymentFocused(true)}
                                        onBlur={(e) => {
                                            setDirectPaymentFocused(false);
                                            const raw = e.target.value;
                                            const n = parseDecimal(raw);
                                            onDirectPaymentGateChange?.(index, {
                                                priceSol: Number.isFinite(n) ? n : 0,
                                                currency: directPaymentCurrencyInput || 'SOL',
                                                tokenMint: directPaymentGate?.tokenMint || '',
                                            });
                                        }}
                                        style={ccStyles.control}
                                    />
                                    <label style={{ marginTop: 6 }}>
                                        <span style={ccStyles.labelHint}>Currency</span>
                                        <select
                                            value={directPaymentCurrencyInput}
                                            onChange={(e) => {
                                                const nextCurrency = e.target.value;
                                                const token = (DONATION_TOKENS || []).find(t => t.symbol === nextCurrency);
                                                const n = parseDecimal(directPaymentPriceInput);
                                                setDirectPaymentCurrencyInput(nextCurrency);
                                                onDirectPaymentGateChange?.(index, {
                                                    priceSol: Number.isFinite(n) ? n : Number(directPaymentGate?.priceSol || 0),
                                                    currency: nextCurrency,
                                                    tokenMint: token?.mint || '',
                                                });
                                            }}
                                            style={ccStyles.control}
                                        >
                                            {(DONATION_TOKENS || []).map((t) => (
                                                <option key={t.symbol} value={t.symbol}>{t.symbol}</option>
                                            ))}
                                        </select>
                                    </label>
                                    <div className="cc-hint" style={{marginTop: 6}}>
                                        Viewers must pay this amount to unlock this panel.
                                    </div>
                                </div>
                            );
                        })()}


                        {gateType !== 'public' && gateType !== 'tokensHeld' && gateType !== 'donation' && gateType !== 'directPayment' && gateType !== 'password' && gateType !== 'scheduledAccess' &&
                        lists.filter(l => l.gateType === gateType).length === 0 && (
                            <div className="cc-hint">
                                No saved {gateType} lists found. Create one in your gate lists.
                            </div>
                        )}
                    </div>


                    {/* Row: Multi-gate (logic + additional gates) */}
                    <div className="cc-row cc-row--stack">
                        <div className="cc-field">
                            <span style={ccStyles.labelHint}>Gate Logic</span>
                            <select
                                value={gateLogic || 'any'}
                                onChange={e => onGateLogicChange?.(index, e.target.value === 'all' ? 'all' : 'any')}
                                style={ccStyles.control}
                            >
                                <option value="any">Allow if ANY passes</option>
                                <option value="all">Require ALL to pass</option>
                            </select>
                        </div>

                        <div className="cc-field" style={{width: '100%'}}>
                            <span style={ccStyles.labelHint}>Additional gates</span>

                            <div className="cc-extra-gates" style={{display: 'grid', gap: 10}}>
                                {(extraGates || []).map((g, gi) => {
                                    const setGate = (patch) => {
                                        const next = [...(extraGates || [])];
                                        next[gi] = {...(next[gi] || {}), ...patch};
                                        onExtraGatesChange?.(index, next);
                                    };

                                    return (
                                        <div key={gi} className="cc-extra-gate-row"
                                             style={{
                                                 display: 'grid',
                                                 gridTemplateColumns: 'minmax(0, 1fr)',
                                                 gap: 10,
                                                 alignItems: 'center'
                                             }}>
                                            <div className="cc-extra-gate-controls" style={{display: 'grid', gap: 8}}>
                                                {/* Type segmented control (same as primary) */}
                                                <div className="segmented" style={{display: 'flex', flexWrap: 'wrap', gap: 8}}>
                                            {gateTypeOptions.map((t) => (
                                                <button
                                                    key={t}
                                                    type="button"
                                                    className={`seg-btn ${g?.type === t ? 'active' : ''}`}
                                                    onClick={() =>
                                                        setGate(buildGateForType(t, g))
                                                    }
                                                >
                                                            {gateTypeLabel(t)}
                                                        </button>
                                                    ))}
                                                </div>


                                                {/* Token / Collection / Compressed Collection use SAVED LIST select */}
                                                {(g?.type === 'token' || g?.type === 'collection' || g?.type === 'compressedCollection') && (
                                                    <label style={ccStyles.label}>
    <span style={ccStyles.labelHint}>
      {g?.type === 'token'
          ? 'Token List'
          : g?.type === 'collection'
              ? 'Collection List'
              : 'Compressed Collection List'}
    </span>
                                                        <select
                                                            value={g?.listId || ''}
                                                            onChange={(e) => setGate({listId: e.target.value || ''})}
                                                            className="muulti-input"
                                                            style={ccStyles.control}
                                                        >
                                                            <option value="">— select
                                                                saved {g.type === 'compressedCollection' ? 'compressed collection' : g.type} list
                                                                —
                                                            </option>
                                                            {(lists || [])
                                                                .filter((gl) => gl.gateType === g.type)
                                                                .map((gl) => (
                                                                    <option key={gl.id} value={gl.id}>
                                                                        {gl.name || gl.id}
                                                                    </option>
                                                                ))}
                                                        </select>
                                                    </label>
                                                )}

                                                {g?.type === 'tokensHeld' && (() => {
                                                    const heldCurrency = g?.currency || 'SOL';
                                                    const heldToken = (DONATION_TOKENS || []).find(t => t.symbol === heldCurrency);
                                                    return (
                                                        <label style={ccStyles.label}>
                                                            <span style={ccStyles.labelHint}>Token</span>
                                                            <select
                                                                value={heldCurrency}
                                                                onChange={(e) => {
                                                                    const nextCurrency = e.target.value;
                                                                    const token = (DONATION_TOKENS || []).find(t => t.symbol === nextCurrency);
                                                                    setGate({
                                                                        currency: nextCurrency,
                                                                        tokenMint: token?.mint || '',
                                                                        tokenThreshold: Number(g?.tokenThreshold || 0),
                                                                        listId: '',
                                                                    });
                                                                }}
                                                                className="muulti-input"
                                                                style={ccStyles.control}
                                                            >
                                                                {(DONATION_TOKENS || []).map((t) => (
                                                                    <option key={t.symbol} value={t.symbol}>{t.symbol}</option>
                                                                ))}
                                                            </select>
                                                            <span style={{ ...ccStyles.labelHint, marginTop: 8 }}>Minimum Held ({heldCurrency})</span>
                                                            <input
                                                                className="muulti-input"
                                                                type="number"
                                                                min="0"
                                                                step="0.000001"
                                                                value={Number(g?.tokenThreshold || 0)}
                                                                onChange={e => setGate({
                                                                    tokenThreshold: Number(e.target.value || 0),
                                                                    currency: heldCurrency,
                                                                    tokenMint: heldToken?.mint || '',
                                                                    listId: '',
                                                                })}
                                                                style={ccStyles.control}
                                                            />
                                                        </label>
                                                    );
                                                })()}

                                                {/* Donation fields (same as primary donation) */}
                                                {g?.type === 'donation' && (() => {
                                                    const extraCampaign = (donationCampaignOptions || []).find(
                                                        (opt) => String(opt.id) === String(g?.donationCompId || '')
                                                    );
                                                    const extraCurrency = extraCampaign?.currency || 'SOL';
                                                    return (
                                                        <div
                                                            style={{
                                                                display: 'grid',
                                                                gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                                                                gap: 8
                                                            }}>
                                                            <select
                                                                value={String(g?.donationCompId || '')}
                                                                onChange={e => {
                                                                    const nextId = e.target.value;
                                                                    const selected = (donationCampaignOptions || []).find(
                                                                        (opt) => String(opt.id) === String(nextId)
                                                                    );
                                                                    setGate({
                                                                        donationCompId: nextId,
                                                                        campaignName: selected?.campaignName || '',
                                                                        currency: selected?.currency || '',
                                                                        tokenMint: selected?.tokenMint || '',
                                                                    });
                                                                }}
                                                                className="muulti-input"
                                                                style={ccStyles.control}
                                                            >
                                                                <option value="">— select campaign —</option>
                                                                {(donationCampaignOptions || []).map(opt => (
                                                                    <option key={opt.id}
                                                                            value={opt.id}>{opt.label || opt.id}</option>
                                                                ))}
                                                            </select>
                                                            <input
                                                                className="muulti-input"
                                                                type="number"
                                                                min="0"
                                                                step="0.0001"
                                                                placeholder={`Threshold (${extraCurrency})`}
                                                                value={Number(g?.donationThreshold || 0)}
                                                                onChange={e => setGate({
                                                                    donationThreshold: Number(e.target.value || 0),
                                                                    campaignName: g?.campaignName || extraCampaign?.campaignName || '',
                                                                    currency: g?.currency || extraCampaign?.currency || '',
                                                                    tokenMint: g?.tokenMint || extraCampaign?.tokenMint || '',
                                                                })}
                                                                style={ccStyles.control}
                                                            />
                                                        </div>
                                                    );
                                                })()}

                                                {g?.type === 'password' && (
                                                    <label style={ccStyles.label}>
                                                        <span style={ccStyles.labelHint}>Password</span>
                                                        <input
                                                            className="muulti-input"
                                                            type="text"
                                                            autoComplete="off"
                                                            value={String(g?.password || '')}
                                                            onChange={(e) =>
                                                                setGate({
                                                                    password: e.target.value,
                                                                    targetId: String(g?.targetId || compId || index),
                                                                })
                                                            }
                                                            style={ccStyles.control}
                                                        />
                                                        <span style={{ ...ccStyles.labelHint, marginTop: 8 }}>Prompt Text (optional)</span>
                                                        <input
                                                            className="muulti-input"
                                                            type="text"
                                                            autoComplete="off"
                                                            value={String(g?.passwordPrompt || '')}
                                                            onChange={(e) =>
                                                                setGate({
                                                                    passwordPrompt: e.target.value,
                                                                    targetId: String(g?.targetId || compId || index),
                                                                })
                                                            }
                                                            placeholder="Enter password to unlock this panel."
                                                            style={ccStyles.control}
                                                        />
                                                    </label>
                                                )}

                                                {g?.type === 'scheduledAccess' && (
                                                    <label style={ccStyles.label}>
                                                        <span style={ccStyles.labelHint}>Schedule Name</span>
                                                        <input
                                                            className="muulti-input"
                                                            type="text"
                                                            autoComplete="off"
                                                            value={String(g?.scheduleName || '')}
                                                            onChange={(e) =>
                                                                setGate({
                                                                    scheduleName: e.target.value,
                                                                })
                                                            }
                                                            placeholder="Early Access Window"
                                                            style={ccStyles.control}
                                                        />
                                                        <span style={{ ...ccStyles.labelHint, marginTop: 8 }}>Start Date & Time</span>
                                                        <input
                                                            className="muulti-input"
                                                            type="datetime-local"
                                                            required
                                                            value={String(g?.startAt || '')}
                                                            onChange={(e) =>
                                                                setGate({
                                                                    startAt: e.target.value,
                                                                })
                                                            }
                                                            style={ccStyles.control}
                                                        />
                                                        <span style={{ ...ccStyles.labelHint, marginTop: 8 }}>End Date & Time (optional)</span>
                                                        <input
                                                            className="muulti-input"
                                                            type="datetime-local"
                                                            value={String(g?.endAt || '')}
                                                            onChange={(e) =>
                                                                setGate({
                                                                    endAt: e.target.value,
                                                                })
                                                            }
                                                            style={ccStyles.control}
                                                        />
                                                    </label>
                                                )}

                                                {/* Direct Payment fields (additional gate) */}
                                                {g?.type === 'directPayment' && (() => {
                                                    return (
                                                        <label style={ccStyles.label}>
                                                            <span style={ccStyles.labelHint}>Price ({extraCurrencyInputs[String(gi)] || g?.currency || 'SOL'})</span>
                                                            <input
                                                                className="muulti-input"
                                                                type="text"
                                                                inputMode="decimal"
                                                                value={extraPriceInputs[String(gi)] ?? (g?.priceSol != null ? String(g.priceSol) : '')}
                                                                onChange={(e) => {
                                                                    const raw = e.target.value;
                                                                    onDirectPaymentDraftChange?.({
                                                                        compId,
                                                                        index,
                                                                        scope: "extra",
                                                                        extraIndex: gi,
                                                                        priceSol: parseDecimal(raw),
                                                                    });
                                                                    setExtraPriceInputs((prev) => ({
                                                                        ...prev,
                                                                        [String(gi)]: raw,
                                                                    }));
                                                                }}
                                                                onFocus={() => setExtraPriceFocused((prev) => ({ ...prev, [String(gi)]: true }))}
                                                                onBlur={(e) => {
                                                                    setExtraPriceFocused((prev) => ({ ...prev, [String(gi)]: false }));
                                                                    const raw = e.target.value;
                                                                    const n = parseDecimal(raw);
                                                                    setGate({
                                                                        priceSol: Number.isFinite(n) ? n : 0,
                                                                        payToUid: g?.payToUid || ownerUid,
                                                                        targetId: g?.targetId || String(compId ?? index),
                                                                        currency: g?.currency || 'SOL',
                                                                        tokenMint: g?.tokenMint || '',
                                                                    });
                                                                }}
                                                            />
                                                            <div style={{ marginTop: 6 }}>
                                                                <span style={ccStyles.labelHint}>Currency</span>
                                                                <select
                                                                value={extraCurrencyInputs[String(gi)] || g?.currency || 'SOL'}
                                                                onChange={(e) => {
                                                                    const nextCurrency = e.target.value;
                                                                    const token = (DONATION_TOKENS || []).find(t => t.symbol === nextCurrency);
                                                                    const n = parseDecimal(extraPriceInputs[String(gi)] ?? g?.priceSol ?? 0);
                                                                    setExtraCurrencyInputs((prev) => ({
                                                                        ...prev,
                                                                        [String(gi)]: nextCurrency,
                                                                    }));
                                                                    setGate({
                                                                        priceSol: Number.isFinite(n) ? n : Number(g?.priceSol || 0),
                                                                        payToUid: g?.payToUid || ownerUid,
                                                                        targetId: g?.targetId || String(compId ?? index),
                                                                            currency: nextCurrency,
                                                                            tokenMint: token?.mint || '',
                                                                        });
                                                                    }}
                                                                    style={ccStyles.control}
                                                                >
                                                                    {(DONATION_TOKENS || []).map((t) => (
                                                                        <option key={t.symbol} value={t.symbol}>{t.symbol}</option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                        </label>
                                                    );
                                                })()}


                                            </div>

                                            {/* Remove gate */}
                                            <button
                                                type="button"
                                                onClick={() => onExtraGatesChange?.(index, (extraGates || []).filter((_, i) => i !== gi))}
                                                title="Remove gate"
                                                style={{
                                                    width: 36,
                                                    height: 36,
                                                    borderRadius: 10,
                                                    border: '1px solid var(--muulti-border, #294)',
                                                    background: 'transparent',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    );
                                })}

                                <div>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            onExtraGatesChange?.(index, [...(extraGates || []), buildGateForType('token')]);
                                        }}
                                        className="muulti-button"
                                        style={ccStyles.button}
                                    >
                                        + Add gate
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </ComponentControlSection>


            </div>
        </div>

    )
        ;
}
